// src/client/renderer/vfx/env/trophy-shine-pool.ts
//
// Plan 18, Task 5.2 — Trophy shine ceremony pool.
//
// Behaviour contract:
//   - CEREMONY-mode pool. mode: 'ceremony'. Triggered by TrophyShineEvent.
//   - Depends on optional getTrophyMesh?() callback supplied by Plan 22 / Plan 24c.
//     When absent or returning undefined: acquire returns null (graceful no-op).
//   - SINGLE-INSTANCE: only one trophy shine at a time. F6 re-entry guard: new
//     acquire while active releases old animation first (restores emissive baseline).
//   - Animation (gsap timeline, ~2.5-second life):
//       emissiveIntensity: baseline → 2.5×baseline (0.4s bright flash)
//                                   → 0.7×baseline (0.6s dim pulse)
//                                   → baseline (1.5s restore)  [total 2.5s]
//       Rim-light RingGeometry: opacity 0→1 over 200ms (fade in at t=0)
//                               opacity 1→0 over 2.3s (fade out t=0.2 → t=2.5)
//   - CRITICAL: pre-shine emissive captured BEFORE animation, restored on
//     completion AND on dispose. Pool MUST NOT call geometry.dispose() or
//     material.dispose() on the trophy mesh (Plan 8a's GLB loader owns those).
//     Pool MAY dispose its OWN ring geometry/material (pool-owned resources).
//   - isFinished() returns true once the timeline completes.
//   - release(emitter): restores original emissiveIntensity (idempotent).

import * as THREE from 'three';
import type {
  EnvCeremonyPool,
  EnvCeremonyPoolDeps,
  EnvCeremonySpawnArgs,
  EnvCeremonyPoolId,
} from './env-ceremony-types';
import type { CeremonyEvent } from './ceremony-events';
import type { GsapRegistry } from '../gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Total timeline duration (seconds). */
const LIFETIME = 2.5;

/** Emissive triple-pulse phases (durations sum to LIFETIME = 2.5s). */
const PHASE_BRIGHT_FLASH = 0.4; // baseline → 2.5× baseline
const PHASE_DIM_PULSE = 0.6; // 2.5× → 0.7× baseline
// PHASE_RESTORE = 1.5s: 0.7× → baseline; total = 0.4+0.6+1.5 = 2.5

/** Rim-light ring geometry dimensions (fixed radius; close enough to trophy bounding box). */
const RING_INNER_RADIUS = 1.2;
const RING_OUTER_RADIUS = 1.5;
const RING_SEGMENTS = 32;

/** Rim-light fade timing. */
const RING_FADE_IN_DURATION = 0.2; // 0 → 1 opacity
const RING_FADE_OUT_START = 0.2; // start fading out at t=0.2s
const RING_FADE_OUT_DURATION = 2.3; // 1 → 0 opacity (t=0.2 to t=2.5)

// ─────────────────────────────────────────────────────────────────────────────
// Config — extends pool deps with optional trophy mesh callback
// ─────────────────────────────────────────────────────────────────────────────

export interface TrophyShinePoolConfig extends EnvCeremonyPoolDeps {
  /** Returns the trophy Object3D from Plan 8a's GLB loader. Optional — pool is
   *  a no-op when absent or returning undefined. */
  readonly getTrophyMesh?: () => THREE.Object3D | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface ShineEmitterState {
  group: THREE.Group;
  ringMesh: THREE.Mesh;
  ringGeometry: THREE.RingGeometry;
  ringMaterial: THREE.MeshBasicMaterial;
  trophyMesh: THREE.Mesh;
  baselineEmissive: number;
  tlHandle: ReturnType<GsapRegistry['create']>;
  elapsed: number;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility — find first Mesh with MeshStandardMaterial inside an Object3D
// ─────────────────────────────────────────────────────────────────────────────

function findTrophyMeshWithStandardMat(obj: THREE.Object3D): THREE.Mesh | null {
  // If the object itself is a Mesh with MeshStandardMaterial, use it directly.
  if (obj instanceof THREE.Mesh) {
    const mat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
    if (mat instanceof THREE.MeshStandardMaterial) return obj;
  }

  // Traverse children to find the first qualifying Mesh.
  let found: THREE.Mesh | null = null;
  obj.traverse((child) => {
    if (found) return;
    if (child instanceof THREE.Mesh) {
      const mat = Array.isArray(child.material) ? child.material[0] : child.material;
      if (mat instanceof THREE.MeshStandardMaterial) {
        found = child;
      }
    }
  });
  return found;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createTrophyShinePool({
  scene,
  registry,
  getTrophyMesh,
}: TrophyShinePoolConfig): EnvCeremonyPool {
  // Single-instance state
  let currentEmitter: THREE.Object3D | null = null;
  let currentState: ShineEmitterState | null = null;
  let disposed = false;

  // ─────────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────────────────────────────

  function releaseState(state: ShineEmitterState): void {
    // Kill the gsap timeline
    state.tlHandle.dispose();

    // Restore trophy emissive to baseline (non-ownership — do NOT dispose trophy)
    const mat = Array.isArray(state.trophyMesh.material)
      ? (state.trophyMesh.material[0] as THREE.MeshStandardMaterial)
      : (state.trophyMesh.material as THREE.MeshStandardMaterial);
    mat.emissiveIntensity = state.baselineEmissive;

    // Remove ring from scene and dispose pool-owned resources
    scene.remove(state.group);
    state.ringGeometry.dispose();
    state.ringMaterial.dispose();

    state.finished = true;
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(_args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Graceful no-op if trophy callback absent or returns nothing
    if (!getTrophyMesh) return null;
    const trophyObj = getTrophyMesh();
    if (!trophyObj) return null;

    // Find the first Mesh with MeshStandardMaterial inside the trophy object
    const trophyMesh = findTrophyMeshWithStandardMat(trophyObj);
    if (!trophyMesh) return null;

    // F6 re-entry guard: release existing animation before starting a fresh one
    if (currentEmitter !== null && currentState !== null) {
      releaseState(currentState);
      currentEmitter = null;
      currentState = null;
    }

    // Capture baseline emissiveIntensity BEFORE animation
    const mat = Array.isArray(trophyMesh.material)
      ? (trophyMesh.material[0] as THREE.MeshStandardMaterial)
      : (trophyMesh.material as THREE.MeshStandardMaterial);
    const baselineEmissive = mat.emissiveIntensity;

    // ── Build rim-light ring (pool-owned) ──────────────────────────────────
    const ringGeometry = new THREE.RingGeometry(
      RING_INNER_RADIUS,
      RING_OUTER_RADIUS,
      RING_SEGMENTS,
    );
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffd700, // gold tint
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const ringMesh = new THREE.Mesh(ringGeometry, ringMaterial);

    // Position ring at trophy's world position, horizontal (Y-up plane)
    const trophyWorldPos = new THREE.Vector3();
    trophyObj.getWorldPosition(trophyWorldPos);
    ringMesh.position.copy(trophyWorldPos);
    ringMesh.rotation.x = -Math.PI / 2; // lay flat in XZ plane

    // ── Build group ────────────────────────────────────────────────────────
    const group = new THREE.Group();
    group.add(ringMesh);
    scene.add(group);

    // ── Build gsap timeline ────────────────────────────────────────────────
    const tlHandle = registry.create('trophy_shine');
    const tl = tlHandle.timeline;

    // Proxy for emissiveIntensity — GSAP cannot animate Three.js properties
    // directly through material references in all environments.
    const emissiveProxy = { value: baselineEmissive };

    // ── Emissive triple-pulse curve ────────────────────────────────────────
    // Phase 1: baseline → 2.5× baseline (bright flash, 0 to 0.4s)
    tl.to(
      emissiveProxy,
      {
        value: baselineEmissive * 2.5,
        duration: PHASE_BRIGHT_FLASH,
        ease: 'power2.out',
        onUpdate: () => {
          mat.emissiveIntensity = emissiveProxy.value;
        },
      },
      0,
    );

    // Phase 2: 2.5× → 0.7× baseline (dim pulse, 0.4 to 1.0s)
    const dimPulseStart = PHASE_BRIGHT_FLASH;
    tl.to(
      emissiveProxy,
      {
        value: baselineEmissive * 0.7,
        duration: PHASE_DIM_PULSE,
        ease: 'power1.inOut',
        onUpdate: () => {
          mat.emissiveIntensity = emissiveProxy.value;
        },
      },
      dimPulseStart,
    );

    // Phase 3: 0.7× → baseline (restore, 1.0 to 2.5s)
    const restoreStart = PHASE_BRIGHT_FLASH + PHASE_DIM_PULSE;
    const restoreDuration = LIFETIME - restoreStart; // 1.5s
    tl.to(
      emissiveProxy,
      {
        value: baselineEmissive,
        duration: restoreDuration,
        ease: 'power1.inOut',
        onUpdate: () => {
          mat.emissiveIntensity = emissiveProxy.value;
        },
      },
      restoreStart,
    );

    // ── Rim-light ring fade (parallel) ─────────────────────────────────────
    // Fade in: 0 → 1 opacity over 0.2s starting at t=0
    tl.to(
      ringMaterial,
      {
        opacity: 1,
        duration: RING_FADE_IN_DURATION,
        ease: 'power1.out',
      },
      0,
    );
    // Fade out: 1 → 0 opacity over 2.3s starting at t=0.2s
    tl.to(
      ringMaterial,
      {
        opacity: 0,
        duration: RING_FADE_OUT_DURATION,
        ease: 'power1.in',
      },
      RING_FADE_OUT_START,
    );

    // ── Completion marker at absolute 2.5s (NEVER eventCallback) ──────────
    const state: ShineEmitterState = {
      group,
      ringMesh,
      ringGeometry,
      ringMaterial,
      trophyMesh,
      baselineEmissive,
      tlHandle,
      elapsed: 0,
      finished: false,
    };

    tl.call(
      () => {
        // Ensure emissive is exactly at baseline on completion
        mat.emissiveIntensity = baselineEmissive;
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    currentEmitter = group;
    currentState = state;

    return group;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed) return new Set();

    if (currentState && currentEmitter) {
      currentState.elapsed += deltaSeconds;
      // Advance gsap timeline manually (manual-ticker pattern per gsap-registry.ts)
      currentState.tlHandle.timeline.time(currentState.elapsed);

      if (currentState.elapsed >= LIFETIME || currentState.finished) {
        currentState.finished = true;
      }

      return new Set([currentEmitter]);
    }

    return new Set();
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    if (emitter !== currentEmitter) {
      // Stale ref or unknown object — treat as finished
      return true;
    }
    return currentState ? currentState.finished : true;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (emitter !== currentEmitter) return; // safe no-op for stale/unknown refs
    if (currentState) {
      releaseState(currentState);
    }
    currentEmitter = null;
    currentState = null;
  }

  // ── dispose (F4) ───────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    if (currentState) {
      releaseState(currentState);
      currentEmitter = null;
      currentState = null;
    }

    // CRITICAL: do NOT call geometry.dispose() or material.dispose() on the
    // trophy mesh — those resources belong to Plan 8a's GLB loader.
  }

  return { mode: 'ceremony', acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a CeremonyEvent to trophy-shine spawn args.
 * Returns null for any event other than trophy_shine.
 */
export function resolveTrophyShineForEvent(
  event: CeremonyEvent,
): { poolId: EnvCeremonyPoolId; args: EnvCeremonySpawnArgs } | null {
  if (event.type !== 'trophy_shine') return null;

  return {
    poolId: 'trophy_shine',
    args: {},
  };
}
